import { useEffect } from "react";
import { buildSeoDescriptor, type SeoRoute } from "../../shared/seo";

export function useSeo(route: SeoRoute): void {
  const descriptor = buildSeoDescriptor(route, typeof window === "undefined" ? "http://localhost" : window.location.origin);
  const jsonLd = descriptor.jsonLd ? JSON.stringify(descriptor.jsonLd) : "";

  useEffect(() => {
    document.title = descriptor.title;

    setMetaTag('meta[data-seo="application-name"]', {
      content: descriptor.applicationName,
      name: "application-name",
    });
    setMetaTag('meta[data-seo="description"]', {
      content: descriptor.description,
      name: "description",
    });
    setMetaTag('meta[data-seo="robots"]', {
      content: descriptor.robots,
      name: "robots",
    });
    setLinkTag('link[data-seo="canonical"]', {
      href: descriptor.canonicalUrl,
      rel: "canonical",
    });
    setMetaTag('meta[data-seo="og:type"]', {
      content: descriptor.openGraph.type,
      property: "og:type",
    });
    setMetaTag('meta[data-seo="og:site_name"]', {
      content: descriptor.openGraph.siteName,
      property: "og:site_name",
    });
    setMetaTag('meta[data-seo="og:title"]', {
      content: descriptor.openGraph.title,
      property: "og:title",
    });
    setMetaTag('meta[data-seo="og:description"]', {
      content: descriptor.openGraph.description,
      property: "og:description",
    });
    setMetaTag('meta[data-seo="og:url"]', {
      content: descriptor.openGraph.url,
      property: "og:url",
    });
    setMetaTag('meta[data-seo="og:image"]', {
      content: descriptor.openGraph.imageUrl,
      property: "og:image",
    });
    setMetaTag('meta[data-seo="og:image:alt"]', {
      content: descriptor.openGraph.imageAlt,
      property: "og:image:alt",
    });
    setMetaTag('meta[data-seo="og:image:type"]', {
      content: descriptor.openGraph.imageType,
      property: "og:image:type",
    });
    setMetaTag('meta[data-seo="og:image:width"]', {
      content: String(descriptor.openGraph.imageWidth),
      property: "og:image:width",
    });
    setMetaTag('meta[data-seo="og:image:height"]', {
      content: String(descriptor.openGraph.imageHeight),
      property: "og:image:height",
    });
    setMetaTag('meta[data-seo="twitter:card"]', {
      content: descriptor.twitter.card,
      name: "twitter:card",
    });
    setMetaTag('meta[data-seo="twitter:title"]', {
      content: descriptor.twitter.title,
      name: "twitter:title",
    });
    setMetaTag('meta[data-seo="twitter:description"]', {
      content: descriptor.twitter.description,
      name: "twitter:description",
    });
    setMetaTag('meta[data-seo="twitter:image"]', {
      content: descriptor.twitter.imageUrl,
      name: "twitter:image",
    });
    setMetaTag('meta[data-seo="twitter:image:alt"]', {
      content: descriptor.twitter.imageAlt,
      name: "twitter:image:alt",
    });
    setStructuredData('script[data-seo="json-ld"]', jsonLd);
  }, [
    descriptor.applicationName,
    descriptor.canonicalUrl,
    descriptor.description,
    descriptor.openGraph.description,
    descriptor.openGraph.imageAlt,
    descriptor.openGraph.imageHeight,
    descriptor.openGraph.imageType,
    descriptor.openGraph.imageUrl,
    descriptor.openGraph.imageWidth,
    descriptor.openGraph.siteName,
    descriptor.openGraph.title,
    descriptor.openGraph.type,
    descriptor.openGraph.url,
    descriptor.robots,
    descriptor.title,
    descriptor.twitter.card,
    descriptor.twitter.description,
    descriptor.twitter.imageAlt,
    descriptor.twitter.imageUrl,
    descriptor.twitter.title,
    jsonLd,
  ]);
}

function setMetaTag(selector: string, attributes: Record<string, string>): void {
  const element = ensureHeadElement("meta", selector, { "data-seo": selectorToKey(selector), ...attributes });

  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value);
  }
}

function setLinkTag(selector: string, attributes: Record<string, string>): void {
  const element = ensureHeadElement("link", selector, { "data-seo": selectorToKey(selector), ...attributes });

  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value);
  }
}

function setStructuredData(selector: string, jsonLd: string): void {
  const element = ensureHeadElement("script", selector, {
    "data-seo": selectorToKey(selector),
    type: "application/ld+json",
  });
  element.textContent = jsonLd;
}

function ensureHeadElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  selector: string,
  attributes: Record<string, string>,
): HTMLElementTagNameMap[K] {
  const existing = document.head.querySelector(selector);
  if (existing) {
    return existing as HTMLElementTagNameMap[K];
  }

  const element = document.createElement(tagName);
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value);
  }
  document.head.appendChild(element);
  return element;
}

function selectorToKey(selector: string): string {
  return selector.replace(/^.+data-seo="/, "").replace(/".+$/, "");
}
